import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft, Barbell, ListChecks, Clock, DownloadSimple, User as UserIcon, CheckCircle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ImportProgram() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const upperCode = (code || "").toUpperCase();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Bounce to login, then send them back here after auth.
      navigate(`/login?next=${encodeURIComponent(`/coach/import/${upperCode}`)}`, { replace: true });
      return;
    }
    setLoading(true);
    api.get(`/coach/programs/shared/${upperCode}`)
      .then(({ data }) => { setPreview(data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "Share code not found"))
      .finally(() => setLoading(false));
  }, [upperCode, user, authLoading, navigate]);

  const doImport = async () => {
    if (!preview) return;
    if (preview.is_own) {
      toast.info("This is your own program — no import needed");
      return;
    }
    setImporting(true);
    try {
      await api.post(`/coach/programs/import/${upperCode}`);
      toast.success(`"${preview.program.name}" added to your library`);
      navigate("/coach/programs", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="import-loading">
      <div className="label-tiny text-text-secondary">Loading shared program...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="import-error">
      <div className="tactical-card p-8 max-w-md text-center">
        <div className="label-tiny text-brand-heat mb-2">// UNABLE TO OPEN</div>
        <h1 className="font-heading text-3xl mb-2">Share code invalid</h1>
        <p className="text-text-secondary text-sm mb-4">{error}</p>
        <p className="text-text-secondary text-xs mb-4">
          Code <span className="font-mono text-brand-accent">{upperCode}</span> is either expired, revoked, or never existed.
        </p>
        <Link to="/coach/programs" className="inline-flex items-center gap-1.5 border border-brand-accent text-brand-accent px-4 py-2 text-xs label-tiny tracking-wider hover:bg-brand-accent hover:text-surface-base">
          <CaretLeft size={12} /> BACK TO PROGRAMS
        </Link>
      </div>
    </div>
  );

  const p = preview.program;
  const author = preview.author;

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6" data-testid="import-program-page">
      <div>
        <Link to="/coach/programs" className="text-xs label-tiny text-text-secondary hover:text-brand-accent inline-flex items-center gap-1 mb-3">
          <CaretLeft size={12} /> BACK TO PROGRAMS
        </Link>
        <div className="label-tiny text-brand-accent mb-1">// SHARED PROGRAM · CODE {upperCode}</div>
        <h1 className="font-heading text-4xl sm:text-5xl">{p.name?.toUpperCase()}</h1>
        <div className="text-sm text-text-secondary mt-2 flex items-center gap-2 flex-wrap">
          <UserIcon size={14} weight="duotone" className="text-brand-accent" />
          <span>Shared by <span className="text-brand-accent font-medium">{author.name}</span></span>
          {author.specialty && <span className="label-tiny text-text-secondary">· {author.specialty}</span>}
          {preview.is_own && <span className="label-tiny text-brand-heat">· This is your own program</span>}
        </div>
      </div>

      {/* Preview tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Clock}      label="Weeks"       value={p.weeks || "—"} />
        <Tile icon={Barbell}    label="Days / Week" value={p.days_per_week || "—"} />
        <Tile icon={ListChecks} label="Total Days"  value={p.day_count} />
        <Tile icon={Barbell}    label="Exercises"   value={p.exercise_count} />
      </div>

      {/* Description */}
      {(p.description || p.goal) && (
        <div className="tactical-card p-5">
          {p.goal && <div className="label-tiny text-brand-accent mb-1">// GOAL</div>}
          {p.goal && <div className="text-sm mb-3">{p.goal}</div>}
          {p.description && <div className="label-tiny text-brand-accent mb-1">// DESCRIPTION</div>}
          {p.description && <div className="text-sm whitespace-pre-wrap text-text-secondary">{p.description}</div>}
        </div>
      )}

      {/* Day list preview */}
      {p.days?.length > 0 && (
        <div className="tactical-card p-5" data-testid="import-day-list">
          <div className="label-tiny text-brand-accent mb-3">// SESSION STRUCTURE</div>
          <div className="space-y-2">
            {p.days.slice(0, 12).map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className="label-tiny text-text-secondary text-[10px] w-12 tabular-nums">W{d.week || 1}·D{d.day || i + 1}</span>
                  <span>{d.title || `Session ${i + 1}`}</span>
                </div>
                <span className="label-tiny text-text-secondary text-[10px] tabular-nums">
                  {(d.exercises || []).length} exercises
                </span>
              </div>
            ))}
            {p.days.length > 12 && (
              <div className="label-tiny text-text-secondary text-center pt-2">
                +{p.days.length - 12} more sessions
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import CTA */}
      <div className="tactical-card p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// READY TO USE</div>
          <div className="text-sm">
            A clean copy will be added to your Program Builder library — you can edit it freely without affecting the original.
          </div>
          <div className="label-tiny text-text-secondary text-[10px] mt-1">
            Used {preview.use_count} time{preview.use_count === 1 ? "" : "s"} previously
          </div>
        </div>
        <button
          onClick={doImport}
          disabled={importing || preview.is_own}
          className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-6 py-3 text-sm label-tiny tracking-wider disabled:opacity-50 hover:bg-brand-accent-hover transition"
          data-testid="import-program-button"
        >
          {preview.is_own ? (
            <><CheckCircle size={14} weight="fill" /> ALREADY YOURS</>
          ) : importing ? (
            <>IMPORTING…</>
          ) : (
            <><DownloadSimple size={14} weight="bold" /> IMPORT TO MY LIBRARY</>
          )}
        </button>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="tactical-card p-4">
      <div className="flex items-center gap-2 label-tiny text-text-secondary mb-1">
        <Icon size={12} weight="duotone" className="text-brand-accent" />
        <span>{label.toUpperCase()}</span>
      </div>
      <div className="font-heading text-3xl tabular-nums">{value}</div>
    </div>
  );
}
